'use client';

import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import cn from 'classnames';
import { ImageProps } from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import slugify from 'react-slugify';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import Image from '@/app/_components/Image';
import PauseIcon from '@/app/_icons/pause.svg';
import PlayIcon from '@/app/_icons/play.svg';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import './HeroBanner.scss';
import { useOpenFloatingCTA } from '@/hooks/floating-cta.hook';
import { directusApiUri } from '@/services/api.service';
import { getImageLink } from '@/services/link.service';
import type { ImageTransformationKeys } from '@/types/files/files.types';
import type { NewCarData } from '@/types/new-cars/new-cars.types';
import imageLoader from '@/utils/image-loaders';

import LinkCustom from '../link/LinkCustom';

import styles from './HeroBanner.module.scss';

type HeroBannersT = {
  desktop_image: string;
  mobile_image: string;
  terms_and_conditions: string;
  heading: string;
  subheading: string;
  page: string;
  show_call_to_action: boolean;
  call_to_action_text: string;
  call_to_action_url: string;

  show_call_to_action_2: boolean;
  call_to_action_text_2: string;
  call_to_action_url_2: string;

  show_book_test_drive: boolean;
  test_drive_range: { name: string };
  call_to_action_position?: string;
  video?: string;
};

type Props = {
  imageKeys: ImageTransformationKeys;
  heroBanners: HeroBannersT[];
  newCars: NewCarData[];
  align?: string;
  showOverlay?: boolean;
};

const HeroBanner = React.memo(({ heroBanners, newCars, align = 'left' , showOverlay = true}: Props) => {
  const openLeadForm = useOpenFloatingCTA();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);

    handler();
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const handleSlideChange = (s: any) => {
    const idx = s.realIndex ?? 0;

    setCurrentIndex(idx);

    // Pause all videos first
    Object.keys(videoRefs.current).forEach((k) => {
      const key = Number(k);
      const v = videoRefs.current[key];

      if (v && !v.paused) {
        try {
          v.pause();
        } catch {
          console.error("something worng..!!")          
        }
      }
    });

    // Try to play active video (if present and not mobile)
    setTimeout(() => {
      const activeVideo = videoRefs.current[idx];

      if (activeVideo && !isMobile) {
        activeVideo.play().then(() => {
          setPlayingIndex(idx);
        }).catch(() => {
          // autoplay may be blocked; keep playingIndex null
          setPlayingIndex(null);
        });
      } else {
        setPlayingIndex(null);
      }
    }, 120);
  };

  // toggle play/pause for a given slide index
  const toggleVideo = (index: number, action: 'play' | 'pause') => {
    const v = videoRefs.current[index];

    if (!v) return;
    if (action === 'play') {
      v.play().catch(() => {});
      setPlayingIndex(index);
    } else {
      v.pause();
      setPlayingIndex(null);
    }
  };

  const renderItem = (item: HeroBannersT, index: number, itemAlign?: string) => {
    const alignment = itemAlign || item.call_to_action_position || align || 'left';
    const alignmentClass = `${alignment}Align`;

    return (
      <Box key={`content-${item.desktop_image}`} className={cn(styles.heroBannerContentWrapper, styles[alignmentClass as keyof typeof styles])}>
        <Box className={styles.heroBannerContent}>
          <Box className={styles.headingElement}>
            {item.heading && (
              <Heading
                as='h2'
                className={styles.titleXl}
                marginBottom={{ base: 4, md: 8 }}
                color="white"
                textTransform="capitalize"
              >
                {item.heading}
              </Heading>
            )}
            {item.subheading && (
              <Text
                as="h2"
                className={styles.titleXs}
                color="white"
                marginBottom={6}
                textTransform="capitalize"
              >
                {item.subheading}
              </Text>
            )}
            <div className={styles.buttonContainer}>
              {item.show_call_to_action && item.call_to_action_text ? (
                <LinkCustom className="btnWhite" moduleClass="btnCta" href={item?.call_to_action_url}>
                  {item?.call_to_action_text}
                </LinkCustom>
              ) : null}

              {item?.show_call_to_action_2 && item.call_to_action_text_2 ? (
                <LinkCustom className="btnOutlineWhite" moduleClass="btnCta" href={item?.call_to_action_url_2}>
                  {item?.call_to_action_text_2}
                </LinkCustom>
              ) : null}

              {item?.show_book_test_drive && (
                <ButtonCustom btnText="Book A Test Drive" className="btnOutlineWhite" moduleClass="btnCta" onClick={() => {
                  const activeItem = heroBanners[currentIndex];
                  
                  if (activeItem) {
                    openTestDriveModal(activeItem);
                  }
                }} />
              )}
            </div>
            <Box color={'white'} marginTop={'10px'}>
              <Text>{item.terms_and_conditions}</Text>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const openTestDriveModal = (item: HeroBannersT) => {
    const newCar = newCars?.find((c) => c.slug === slugify(item.test_drive_range?.name ?? ''));

    if (!newCar) {
      return;
    }

    openLeadForm('test-drive', 'TestDrive', {
      type: 'new-car',
      newCar: newCar,
    });
  };

  return (
    <>
      <Swiper
        effect={'fade'}
        autoplay={{
          delay: 20000,
          disableOnInteraction: true,
        }}
        loop={true}
        navigation={true}
        pagination={{ clickable: true }}
        modules={[EffectFade, Navigation, Pagination, Autoplay]}
        breakpoints={{
          0: { slidesPerView: 1, spaceBetween: 20 },
          768: { slidesPerView: 1, spaceBetween: 40 },
          1024: { slidesPerView: 1, spaceBetween: 50 },
        }}
        className="heroSwiper"
        onSlideChange={handleSlideChange}
      >
        {heroBanners &&
          heroBanners.map((item, index) => {
            const bannerId = `banner-${index}-${item.desktop_image || ''}`;
            const qualityToRender = 75;
            const quality = index === 0 ? 30 : qualityToRender;

            const onLoad = (event: any) => {
              const text = `image-q${quality}`;
              const textToReplace = `image-q${qualityToRender}`;

              if (!(event.currentTarget.src || '').includes(text)) {
                return;
              }

              const newSrc = event.currentTarget.src.replace(text, textToReplace);
              const newSrcSet = event.currentTarget.srcset.replaceAll(text, textToReplace);

              event.currentTarget.srcset = newSrcSet;
              event.currentTarget.src = newSrc;
            };

            const imageProps: ImageProps = {
              src: `${directusApiUri}/assets/${item.desktop_image}?__dis=${item.desktop_image}&__mis=${item.mobile_image}`,
              alt: item.desktop_image,
              fill: true,
              placeholder: 'empty',
              priority: index === 0,
              quality,
              loader: imageLoader,
              id: bannerId,
              sizes: '(max-width: 575px) 30vw',
            };

            if (index === 0) imageProps.onLoad = onLoad;

            const itemAlign = item.call_to_action_position ?? align ?? 'left';
            const alignmentClass = cn(styles.heroWrapper, {
              [styles.leftAlign as string]: itemAlign === 'left',
              [styles.rightAlign as string]: itemAlign === 'right',
              [styles.topAlign as string]: itemAlign === 'top',
              [styles.centerAlign as string]: itemAlign === 'centre_bottom' || itemAlign === 'centre' || itemAlign === 'center',
            });

            const videoUrl = item.video ? getImageLink(item.video) : null;

            return (
              <SwiperSlide key={`hero-${item.desktop_image}`}>
                <div 
                  className={cn(
                    styles.heroSwiperWrap,
                    alignmentClass,
                    showOverlay && styles.withOverlay
                  )}>
                  {/* Background image always present (behind video) */}
                  {item.desktop_image && (
                    <div className={styles.bannerImage}>
                      <Image {...imageProps} alt={item.heading || 'Banner image'} title={item.heading || 'Banner image'} priority={index === 0} fill style={{ objectFit: 'cover' }} />
                    </div>
                  )}

                  {/* Video (desktop only). Attach ref per index */}
                  {videoUrl && !isMobile && (
                    <div className={styles.videoPlayer}>
                      <video
                        playsInline
                        ref={(el) => {
                          videoRefs.current[index] = el;
                        }}
                        className={styles.videoPlayerFrame}
                        preload="metadata"
                        autoPlay={index === currentIndex}
                        muted
                        loop
                      >
                        <source src={videoUrl} type="video/mp4" />
                      </video>
                    </div>
                  )}

                  {/* overlay content */}
                  {index === currentIndex && renderItem(item, index, itemAlign)}
                  {videoUrl && !isMobile && (
                    <div className={styles.videoPlayerController}>
                      <Stack gap={2.5} marginEnd={2}>
                        {playingIndex === index ? (
                          <Flex alignItems={'center'} onClick={() => toggleVideo(index, 'pause')} cursor={'pointer'}>
                            <Image alt="pause" height={16} width={16} src={PauseIcon} />
                            <Text as="b" lineHeight={'1'} mt={0.5} fontSize="xs" color={'white'}>
                              PAUSE
                            </Text>
                          </Flex>
                        ) : (
                          <Flex alignItems={'center'} onClick={() => toggleVideo(index, 'play')} cursor={'pointer'}>
                            <Image alt="play" height={16} width={16} src={PlayIcon} title="play" />
                            <Text as="b" lineHeight={'1'} mt={0.5} fontSize="xs" color={'white'}>
                              PLAY
                            </Text>
                          </Flex>
                        )}
                      </Stack>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
      </Swiper>

      {/* Mobile-only heading/content block (keeps existing behavior) */}
      <Box className="headingElementMobile">
        <Box className="headingElement" paddingTop={'64px'} paddingBottom={{ base: '33px', md: 12 }} paddingX={6} textAlign={'center'}>
          {heroBanners[currentIndex]?.heading ? (
            <Heading as="h2" className="titleXl" marginBottom={4} color="white" textTransform="capitalize">
              {heroBanners[currentIndex]?.heading}
            </Heading>
          ) : null}
          {heroBanners[currentIndex]?.subheading && (
            <Text as="h2" className="titleXs" color="white" marginBottom={6} textTransform="capitalize">
              {heroBanners[currentIndex]?.subheading}
            </Text>
          )}

          {/* mobile CTAs */}
          {heroBanners[currentIndex]?.show_call_to_action && heroBanners[currentIndex]?.call_to_action_text && (
            <LinkCustom className="btnWhite" href={heroBanners[currentIndex]?.call_to_action_url} moduleClass="btnCta" extraClass="btnFirst">
              {heroBanners[currentIndex]?.call_to_action_text?.trim()}
            </LinkCustom>
          )}
          {heroBanners[currentIndex]?.show_book_test_drive && (
            <ButtonCustom btnText="Book A Test Drive" className="btnOutlineWhite" moduleClass="btnCta" onClick={() => openTestDriveModal(heroBanners[currentIndex]!)} />
          )}
          {heroBanners[currentIndex]?.show_call_to_action_2 && heroBanners[currentIndex]?.call_to_action_text_2 && (
            <LinkCustom className="btnWhite" moduleClass="btnCta" href={heroBanners[currentIndex].call_to_action_url_2}>
              {heroBanners[currentIndex]?.call_to_action_text_2?.trim()}
            </LinkCustom>
          )}
           <Box color={'white'} marginTop={'10px'}>
            <Text>{heroBanners[currentIndex]?.terms_and_conditions}</Text>
          </Box>
        </Box>
      </Box>
    </>
  );
});

HeroBanner.displayName = 'HeroBanner';

export default HeroBanner;
